import React, { useState } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import Currency from 'components/currency';
import { CurrencyLineSkeleton } from 'pages/cart/currency-line-skeleton';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode } from 'state/order';
import { computeDeliveryFee } from 'utils/cart/helper';

import { DeliveryFeesModal } from '../../delivery-fees-modal';
import { IconContainer } from '../styled';

import DeliveryFees from './delivery-fees';
import { Total, TotalLabel, TotalsRow } from './styled';
import TaxesRow from './taxes-row';

const geographicalFeeLabelIdMap = { CO: 'geographicalFeeCO' };

const PadLeftContainer = styled.div`
  padding-left: 1rem;
`;

interface TaxesAndFeesProps {
  deliveryFee: number | null;
  deliveryFeeDiscount: number | null;
  dropoffState?: string | null;
  serviceFeeCents: number;
  smallCartFeeCents: number;
  geographicalFeeCents: number;
  taxCents: number;
  serviceMode: ServiceMode | null;
  tipAmount: number;
  subTotalCents: number;
  isLoading?: boolean;
}

const TaxesAndFees: React.FC<TaxesAndFeesProps> = ({
  isLoading,
  deliveryFee,
  deliveryFeeDiscount,
  dropoffState,
  serviceFeeCents,
  smallCartFeeCents,
  geographicalFeeCents,
  taxCents,
  tipAmount,
  serviceMode,
  subTotalCents,
}) => {
  const [showDeliveryFeesModal, setShowDeliveryFeesModal] = useState(false);
  const isTwoStepCheckout = useFlag(LaunchDarklyFlag.TWO_STEP_CHECKOUT);
  const enableHideTaxLine = useFlag(LaunchDarklyFlag.ENABLE_HIDE_TAX_LINE);
  const { formatMessage } = useIntl();

  const finalDeliveryFee = computeDeliveryFee({
    feeCents: deliveryFee,
    feeDiscountCents: deliveryFeeDiscount,
    serviceFeeCents,
    smallCartFeeCents,
    geographicalFeeCents,
  });

  const isFreeDelivery = finalDeliveryFee === 0 && !isLoading;
  // Use a custom label for geographical delivery fees as required
  const geographicalFeeLabelId =
    dropoffState && geographicalFeeLabelIdMap.hasOwnProperty(dropoffState)
      ? geographicalFeeLabelIdMap[dropoffState]
      : 'geographicalFee';

  const onClickDeliveryFees = () => {
    setShowDeliveryFeesModal(true);
  };

  return (
    <>
      <TotalsRow>
        {isLoading ? (
          <CurrencyLineSkeleton />
        ) : (
          <>
            <TotalLabel>
              {formatMessage({
                id: 'deliveryFees',
              })}
              <IconContainer
                aria-label={formatMessage({
                  id: 'taxesAndFeesButtonAriaLabel',
                })}
                type="button"
                onClick={onClickDeliveryFees}
              >
                <Icon icon="info" color="icon-default" width="14px" aria-hidden />
              </IconContainer>
            </TotalLabel>
          </>
        )}
      </TotalsRow>
      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        <PadLeftContainer>
          <TotalsRow>
            <TotalLabel isFreeDelivery={isFreeDelivery}>
              {formatMessage({
                id: 'deliveryFee',
              })}
            </TotalLabel>
            <DeliveryFees
              isFreeDelivery={isFreeDelivery}
              deliveryFee={finalDeliveryFee}
              deliveryFeeDiscount={deliveryFeeDiscount}
              isLoading={isLoading}
            />
          </TotalsRow>
          {geographicalFeeCents > 0 && (
            <TotalsRow>
              <TotalLabel>
                {formatMessage({
                  id: geographicalFeeLabelId,
                })}
              </TotalLabel>
              <Total data-testid="geographical-fee-amount">
                <Currency amount={geographicalFeeCents} />
              </Total>
            </TotalsRow>
          )}
          {serviceFeeCents > 0 && (
            <TotalsRow>
              <TotalLabel>
                {formatMessage({
                  id: 'serviceFee',
                })}
              </TotalLabel>
              <Total data-testid="service-fee-amount">
                <Currency amount={serviceFeeCents} />
              </Total>
            </TotalsRow>
          )}
          {smallCartFeeCents > 0 && (
            <TotalsRow>
              {isLoading ? (
                <CurrencyLineSkeleton />
              ) : (
                <>
                  <TotalLabel>
                    {formatMessage({
                      id: 'smallCartFee',
                    })}
                  </TotalLabel>
                  <Total data-testid="small-cart-fee-amount">
                    <Currency amount={smallCartFeeCents} />
                  </Total>
                </>
              )}
            </TotalsRow>
          )}
        </PadLeftContainer>
      )}
      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        !enableHideTaxLine && <TaxesRow taxCents={taxCents} isLoading={isLoading} />
      )}
      {!isTwoStepCheckout && (
        <TotalsRow>
          {isLoading ? (
            <CurrencyLineSkeleton />
          ) : (
            <>
              <TotalLabel>
                {formatMessage({
                  id: 'tip',
                })}{' '}
                <small>
                  (
                  {formatMessage({
                    id: 'optional100PercentGoesToDriver',
                  })}
                  )
                </small>
              </TotalLabel>
              <Total data-testid="tip-amount">
                <Currency amount={tipAmount} />
              </Total>
            </>
          )}
        </TotalsRow>
      )}
      <DeliveryFeesModal
        open={showDeliveryFeesModal}
        onClose={() => setShowDeliveryFeesModal(false)}
        serviceMode={serviceMode}
        subTotalCents={subTotalCents}
        deliveryFeeCents={finalDeliveryFee}
        serviceFeeCents={serviceFeeCents}
        geographicalFeeCents={geographicalFeeCents}
        smallCartFeeCents={smallCartFeeCents}
        isLoading={isLoading}
      />
    </>
  );
};

export default TaxesAndFees;
